/**
 * Coleta de exposição e engajamento do IntelligenceEngine v2.
 *
 * Desligada por padrão. Só envia quando o backend diz `intelligence_tracking: true` e a
 * sessão cai no balde do rollout (`intelligence_tracking_percent`, amostra.js). Fora
 * disso toda função daqui volta sem fazer nada: nenhum observador, nenhum pedido. Com a
 * coleta desligada (percentual 0) nada vai para o localStorage.
 *
 * Usuário logado segue a mesma regra e manda a mesma sessão anônima: nenhum id de conta
 * sai nos eventos. `localStorage sv-interno=1` (equipe) entra sempre e é gravado como
 * `internal_validation`, com peso zero.
 *
 * Três estados de uma oferta, que não se confundem:
 *   renderizada  o card existe no DOM. Não é enviado.
 *   impressão    o card ficou >= 50% visível por 500 ms contínuos (useImpressao).
 *   engajamento  clique na oferta, "Ir para a loja", compartilhar, curtir, buscar, filtrar.
 *
 * Sem dado pessoal: `session_id` é aleatório, fica no localStorage (`sv-sessao`) e vence
 * com 30 min parado. Nada de conta, e-mail ou identificador do aparelho. O termo de busca
 * sai normalizado e sem e-mail, URL ou números longos (o backend normaliza de novo).
 *
 * Envio em lote: até 20 itens ou 2 s, o que vier antes, e sempre ao esconder a página.
 * `navigator.sendBeacon` quando existe; senão `fetch` com `keepalive`.
 */
import { API } from '../config/apiBase';
import { naAmostra } from './amostra';

export const CRITERIO_IMPRESSAO = { fracaoVisivel: 0.5, minVisivelMs: 500 };
export const JANELA_DEDUP_MS = 30 * 60 * 1000;
export const SESSAO_OCIOSA_MS = 30 * 60 * 1000;
export const ROTA_LOTE = '/eventos/lote';
const CHAVE_SESSAO = 'sv-sessao';
const CHAVE_INTERNO = 'sv-interno';
const MAX_FILA = 20;
const ESPERA_MS = 2000;
const MAX_LOTE = 50;

const estado = {
    ativo: false,          // backend ligou e esta sessão participa
    ligada: false,         // backend ligou (intelligence_tracking)
    percentual: 0,
    sessaoAmostrada: null,
    fila: [],
    timer: null,
    vistos: new Map(),
    sessaoMemoria: null,
    enviar: null,
    ouvintes: false,
    assinantes: new Set(),
};

/** Para `useSyncExternalStore`: cards montados antes da flag chegar passam a observar quando ela liga. */
export function assinarColeta(fn) {
    estado.assinantes.add(fn);
    return () => estado.assinantes.delete(fn);
}

const hex = (bytes) => {
    const b = new Uint8Array(bytes);
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) crypto.getRandomValues(b);
    else for (let i = 0; i < b.length; i += 1) b[i] = Math.floor(Math.random() * 256);
    return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
};

export function novoId(prefixo) {
    return `${prefixo}_${hex(8)}`;
}

function lerLocal(chave) {
    try { return window.localStorage.getItem(chave); } catch (_) { return null; }
}

function gravarLocal(chave, valor) {
    try { window.localStorage.setItem(chave, valor); return true; } catch (_) { return false; }
}

export function sessaoAtual(agora = Date.now()) {
    let atual = null;
    try { atual = JSON.parse(lerLocal(CHAVE_SESSAO) || 'null'); } catch (_) { atual = null; }
    if (!atual) atual = estado.sessaoMemoria;
    if (!atual || !/^[a-f0-9]{32}$/.test(atual.id || '') || agora - (atual.ultimo || 0) > SESSAO_OCIOSA_MS) {
        atual = { id: hex(16), ultimo: agora };
    }
    atual.ultimo = agora;
    estado.sessaoMemoria = atual;
    gravarLocal(CHAVE_SESSAO, JSON.stringify(atual));
    return atual.id;
}

export function normalizarBusca(texto, max = 60) {
    return String(texto || '')
        .replace(/[^\s@]+@[^\s@]+/g, ' ')
        .replace(/https?:\/\/\S+|www\.\S+/g, ' ')
        .replace(/\d[\d\s().\-/]{6,}\d/g, ' ')
        .normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9 ]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, max)
        .trim();
}

function enviarPadrao(corpo) {
    const url = `${API}${ROTA_LOTE}`;
    try {
        if (typeof navigator !== 'undefined' && navigator.sendBeacon
            && navigator.sendBeacon(url, new Blob([corpo], { type: 'application/json' }))) {
            return;
        }
    } catch (_) { /* cai no fetch */ }
    try {
        fetch(url, { method: 'POST', body: corpo, keepalive: true, headers: { 'Content-Type': 'application/json' } }).catch(() => {});
    } catch (_) { /* sem rede: o evento se perde, e está tudo bem */ }
}

export function descarregar() {
    if (estado.timer) { clearTimeout(estado.timer); estado.timer = null; }
    while (estado.fila.length) {
        const lote = estado.fila.splice(0, MAX_LOTE);
        (estado.enviar || enviarPadrao)(JSON.stringify({ events: lote }));
    }
}

function aoEsconder() {
    if (document.visibilityState === 'hidden') descarregar();
}

function participa(agora = Date.now()) {
    if (!estado.ligada || estado.percentual <= 0 || typeof window === 'undefined') return false;
    if (lerLocal(CHAVE_INTERNO) === '1') return true;
    const sessao = sessaoAtual(agora);
    estado.sessaoAmostrada = sessao;
    return naAmostra(sessao, estado.percentual);
}

function definirAtivo(valor) {
    const antes = estado.ativo;
    estado.ativo = valor;
    if (antes !== estado.ativo) estado.assinantes.forEach((fn) => fn());
}

/**
 * `ativo`: o backend ligou a coleta. `percentual`: fatia de sessões (0..100); sem ele,
 * 100 (usado nos testes e em ambiente local).
 */
export function configurarColeta({ ativo, percentual, enviar } = {}) {
    estado.ligada = ativo === true;
    estado.percentual = percentual === undefined ? 100 : Math.max(0, Math.min(100, Number(percentual) || 0));
    if (enviar !== undefined) estado.enviar = enviar;
    definirAtivo(participa());
    if (typeof window === 'undefined') return;
    if (estado.ativo && !estado.ouvintes) {
        document.addEventListener('visibilitychange', aoEsconder);
        window.addEventListener('pagehide', descarregar);
        estado.ouvintes = true;
    } else if (!estado.ativo && estado.ouvintes) {
        document.removeEventListener('visibilitychange', aoEsconder);
        window.removeEventListener('pagehide', descarregar);
        estado.ouvintes = false;
        estado.fila = [];
        if (estado.timer) { clearTimeout(estado.timer); estado.timer = null; }
    }
}

export function coletaAtiva() {
    return estado.ativo;
}

/** Só para testes: volta ao estado inicial. */
export function _reiniciarColeta() {
    configurarColeta({ ativo: false, enviar: null });
    estado.vistos = new Map();
    estado.sessaoMemoria = null;
}

/**
 * Enfileira um evento. Devolve false quando a coleta está desligada ou quando é uma
 * impressão repetida (mesma sessão, oferta e superfície em 30 min).
 */
export function registrar(tipo, dados = {}) {
    if (!estado.ativo || typeof window === 'undefined') return false;
    const agora = Date.now();
    const sessao = sessaoAtual(agora);
    if (sessao !== estado.sessaoAmostrada && lerLocal(CHAVE_INTERNO) !== '1') {
        // A sessão girou (30 min parada): o balde é outro, e a participação também.
        estado.sessaoAmostrada = sessao;
        if (!naAmostra(sessao, estado.percentual)) { definirAtivo(false); return false; }
    }
    if (tipo === 'impression') {
        const chave = `${sessao}|${dados.deal_id}|${dados.surface}`;
        const antes = estado.vistos.get(chave);
        if (antes && agora - antes < JANELA_DEDUP_MS) return false;
        estado.vistos.set(chave, agora);
        if (estado.vistos.size > 5000) estado.vistos.clear();
    }
    const item = {
        type: tipo,
        ts: new Date(agora).toISOString(),
        session_id: sessao,
        path: window.location.pathname,
        viewport_w: window.innerWidth,
        channel: 'web',
        ...dados,
    };
    if (tipo === 'search') item.query = normalizarBusca(dados.query);
    if (typeof navigator !== 'undefined' && navigator.webdriver === true) item.webdriver = true;
    if (lerLocal(CHAVE_INTERNO) === '1') item.internal = true;
    estado.fila.push(item);
    if (estado.fila.length >= MAX_FILA) descarregar();
    else if (!estado.timer) estado.timer = setTimeout(descarregar, ESPERA_MS);
    return true;
}

/** "Ir para a loja": registra a intenção e devolve o id que vai na URL de /api/go/deal. */
export function registrarIntencaoCompra(deal, superficie = 'deal_page') {
    if (!estado.ativo || !deal?.deal_id) return null;
    const intentId = novoId('int');
    registrar('purchase_intent', {
        deal_id: deal.deal_id, intent_id: intentId, surface: superficie, marketplace: deal.store_name || '',
    });
    descarregar();
    return intentId;
}

export function registrarFiltro(tipoFiltro, valor) {
    return registrar('filter', { filter_type: tipoFiltro, filter_value: String(valor || '') });
}

/**
 * Compartilhar. `share_intent` sai no toque; `share_completed` só quando o navegador
 * confirma (promessa do share nativo resolvida ou texto copiado).
 */
export function registrarCompartilhamento(dealId, destino, superficie, promessa) {
    registrar('share_intent', { deal_id: dealId, destination: destino, surface: superficie || '' });
    if (promessa && typeof promessa.then === 'function') {
        return promessa.then((r) => {
            registrar('share_completed', { deal_id: dealId, destination: destino, surface: superficie || '' });
            return r;
        });
    }
    return promessa;
}
